import { calculateParentItemChanges } from "utils/calculations";
import { getNextBundleChildPosition, resolveProductServicePosition } from "utils/document/productServices";

const ID_KEY = "document_item_product_service_id";

export function isValidProductServiceId(id) {
  return id != null && id !== "" && String(id) !== "undefined";
}

function getParentId(ps) {
  const parent = ps?.product_service_parent_id;
  if (Array.isArray(parent)) return parent[0] != null ? String(parent[0]) : null;
  if (parent) return String(parent);
  return null;
}

export function isMarkedForDeleteInWizard(itemsToDelete, id) {
  if (id == null) return false;
  return Boolean(itemsToDelete[id] || itemsToDelete[String(id)]);
}

function rebuildParentAfterChildRemoval(ps, remainingChildren) {
  let bundle_group = ps.bundle_group
    ? { ...ps.bundle_group, items: [...(ps.bundle_group.items || [])] }
    : { items: [], default_group_ps_item_id: "" };

  if (bundle_group.items?.length) {
    const remainingIds = new Set(remainingChildren.map((c) => String(c[ID_KEY])));
    const removedDefault =
      bundle_group.default_group_ps_item_id &&
      !remainingIds.has(String(bundle_group.default_group_ps_item_id));
    bundle_group = {
      items: bundle_group.items.filter((id) => remainingIds.has(String(id))),
      default_group_ps_item_id: removedDefault ? "" : bundle_group.default_group_ps_item_id
    };
  }

  const updatedParent = {
    ...ps,
    children: remainingChildren,
    bundle_group,
    is_bundle: remainingChildren.length > 0
  };

  const priceChanges = calculateParentItemChanges(updatedParent, bundle_group, ID_KEY);

  return {
    ...updatedParent,
    ...priceChanges,
    total: Number(priceChanges.sell || 0) * Number(ps.quantity || 1) - Number(ps.discount || 0)
  };
}

function childHasPhotoMetadata(child) {
  if (Array.isArray(child?.photos) && child.photos.length) {
    return true;
  }

  const photo = child?.photo ?? child?.Photo;
  return photo != null && photo !== "" && photo !== "-";
}

function mergeChildrenLists(nestedChildren = [], flatChildren = []) {
  const childMap = new Map();
  nestedChildren.forEach((c) => {
    if (c?.[ID_KEY] != null) childMap.set(String(c[ID_KEY]), c);
  });
  flatChildren.forEach((c) => {
    if (c?.[ID_KEY] == null) return;

    const id = String(c[ID_KEY]);
    const existing = childMap.get(id);
    if (!existing) {
      childMap.set(id, c);
      return;
    }

    const merged = { ...existing, ...c };
    if (childHasPhotoMetadata(existing) && !childHasPhotoMetadata(c)) {
      merged.photos = existing.photos ?? existing.Photos;
      merged.photo = existing.photo ?? existing.Photo ?? null;
    }
    childMap.set(id, merged);
  });
  return [...childMap.values()];
}

function flattenProductServiceIds(productServices) {
  const ids = new Set();
  const walk = (ps) => {
    if (!ps) return;
    if (isValidProductServiceId(ps[ID_KEY])) {
      ids.add(String(ps[ID_KEY]));
    }
    (ps.children || []).forEach(walk);
  };
  (productServices || []).forEach(walk);
  return ids;
}

/**
 * IDs introduced via add_item in this wizard run — never persisted as template rows.
 * Omit from productServiceData.delete; they are excluded from upsert instead.
 */
export function collectNewlyAddedProductServiceIds(itemsToAdd = []) {
  return flattenProductServiceIds(itemsToAdd);
}

/**
 * Full deletion map for in-memory tree updates (template ids, document ids, wizard add_item ids).
 */
export function buildTreeDeletionMap(itemsToDelete = {}, referenceProductServiceId = {}) {
  const map = {};
  Object.entries(itemsToDelete || {}).forEach(([key, marked]) => {
    if (!marked || !isValidProductServiceId(key)) return;
    const resolved = referenceProductServiceId[key];
    if (isValidProductServiceId(resolved)) {
      map[String(resolved)] = true;
    }
    map[String(key)] = true;
  });
  return map;
}

/**
 * Maps template product service IDs to document IDs and drops non-template / invalid entries.
 */
export function resolveItemsToDeleteForApi(
  itemsToDelete = {},
  referenceProductServiceId = {},
  newlyAddedIds = new Set()
) {
  const resolved = {};
  const templateDocumentIds = new Set(
    Object.values(referenceProductServiceId || {}).filter(isValidProductServiceId).map(String)
  );

  Object.entries(itemsToDelete || {}).forEach(([key, marked]) => {
    if (!marked || !isValidProductServiceId(key)) return;

    const mappedId = referenceProductServiceId[key];
    if (isValidProductServiceId(mappedId)) {
      if (!newlyAddedIds.has(String(mappedId))) {
        resolved[mappedId] = true;
      }
      return;
    }

    if (templateDocumentIds.has(String(key)) && !newlyAddedIds.has(String(key))) {
      resolved[key] = true;
    }
  });

  return resolved;
}

/**
 * Collects document product service IDs for productServiceData.delete (template rows only).
 */
export function collectProductServiceDeleteIds(productServices, itemsToDelete = {}) {
  const ids = new Set();

  const walk = (ps) => {
    if (!ps) return;
    const id = ps[ID_KEY];
    if (isValidProductServiceId(id) && isMarkedForDeleteInWizard(itemsToDelete, id)) {
      ids.add(id);
    }
    (ps.children || []).forEach(walk);
  };

  (productServices || []).forEach(walk);

  Object.entries(itemsToDelete).forEach(([id, marked]) => {
    if (marked && isValidProductServiceId(id)) {
      ids.add(id);
    }
  });

  return [...ids];
}

/**
 * Removes deleted rows from itemsToAdd (including nested bundle children) and recalculates parents.
 */
export function stripDeletedFromItemsToAdd(itemsToAdd = [], itemsToDelete = {}) {
  if (!itemsToAdd?.length || !itemsToDelete || !Object.keys(itemsToDelete).length) {
    return itemsToAdd || [];
  }

  const processNode = (ps) => {
    if (!ps) return null;
    if (isMarkedForDeleteInWizard(itemsToDelete, ps[ID_KEY])) return null;

    const children = (ps.children || []).map(processNode).filter(Boolean);
    const childListChanged = children.length !== (ps.children || []).length;

    if (!childListChanged) {
      return ps;
    }

    let updated = { ...ps, children };
    if (updated.is_bundle || children.length) {
      let bundle_group = updated.bundle_group
        ? { ...updated.bundle_group, items: [...(updated.bundle_group.items || [])] }
        : { items: [], default_group_ps_item_id: "" };

      if (bundle_group.items?.length) {
        const remainingIds = new Set(children.map((c) => String(c[ID_KEY])));
        const removedDefault =
          bundle_group.default_group_ps_item_id &&
          !remainingIds.has(String(bundle_group.default_group_ps_item_id));
        bundle_group = {
          items: bundle_group.items.filter((id) => remainingIds.has(String(id))),
          default_group_ps_item_id: removedDefault ? "" : bundle_group.default_group_ps_item_id
        };
      }

      updated = { ...updated, bundle_group, is_bundle: children.length > 0 };
      const priceChanges = calculateParentItemChanges(updated, bundle_group, ID_KEY);
      updated = {
        ...updated,
        ...priceChanges,
        total: Number(priceChanges.sell || 0) * Number(updated.quantity || 1) - Number(updated.discount || 0)
      };
    }

    return updated;
  };

  return itemsToAdd.map(processNode).filter(Boolean);
}

/**
 * Nests top-level bundle children under their parent and recalculates parent pricing.
 * Required before wizard field updates so child formulas roll up to the bundle parent.
 */
export function mergeFlatBundleChildrenIntoParents(productServices) {
  const list = productServices || [];
  if (!list.length) return list;

  const parentIdsInList = new Set(
    list.filter((ps) => !ps?.is_inside_bundle && ps?.[ID_KEY] != null).map((ps) => String(ps[ID_KEY]))
  );

  const flatChildrenByParent = new Map();
  list.forEach((ps) => {
    if (!ps?.is_inside_bundle) return;
    const parentId = getParentId(ps);
    if (!parentId) return;
    if (!flatChildrenByParent.has(parentId)) flatChildrenByParent.set(parentId, []);
    flatChildrenByParent.get(parentId).push(ps);
  });

  return list
    .map((ps) => {
      if (ps?.is_inside_bundle) {
        const parentId = getParentId(ps);
        if (parentId && parentIdsInList.has(parentId)) return null;
        return ps;
      }

      const flatChildren = flatChildrenByParent.get(String(ps[ID_KEY])) || [];
      if (!flatChildren.length) return ps;

      const allChildren = mergeChildrenLists(ps.children || [], flatChildren);
      return rebuildParentAfterChildRemoval({ ...ps, children: allChildren }, allChildren);
    })
    .filter(Boolean);
}

/**
 * When bundle children are marked for deletion, remove them from the parent (nested or flat),
 * update bundle_group, and recalculate parent pricing. Deleted top-level rows are omitted.
 */
export function applyBundleChildRemovals(productServices, itemsToDelete = {}) {
  const nested = mergeFlatBundleChildrenIntoParents(productServices || []);

  if (!nested.length || !itemsToDelete || !Object.keys(itemsToDelete).length) {
    return nested;
  }

  return nested
    .map((ps) => {
      if (ps?.is_inside_bundle) {
        if (isMarkedForDeleteInWizard(itemsToDelete, ps[ID_KEY])) return null;
        return ps;
      }

      if (isMarkedForDeleteInWizard(itemsToDelete, ps?.[ID_KEY])) {
        return null;
      }

      const remainingChildren = (ps.children || []).filter(
        (child) => !isMarkedForDeleteInWizard(itemsToDelete, child?.[ID_KEY])
      );

      if (remainingChildren.length === (ps.children || []).length) {
        return ps;
      }

      return rebuildParentAfterChildRemoval(ps, remainingChildren);
    })
    .filter(Boolean);
}

function resolveProductServiceId(id, referenceProductServiceId = {}) {
  if (id == null || id === "" || id === "none") return null;
  const key = String(id);
  return referenceProductServiceId[key] || key;
}

function getBundleParentIdFromItem(item) {
  const fromParent = item?.product_service_parent_id?.[0];
  if (fromParent != null && fromParent !== "") return fromParent;
  if (item?.add_to_bundle != null && item.add_to_bundle !== "" && item.add_to_bundle !== "none") {
    return item.add_to_bundle;
  }
  return null;
}

function appendChildToBundleParent(parent, child) {
  const childId = String(child[ID_KEY]);
  const existingChildren = parent.children || [];
  if (existingChildren.some((c) => String(c[ID_KEY]) === childId)) {
    return parent;
  }

  const { bundle_group: _childBundleGroup, bundle_group_items: _bgItems, ...childRest } = child;
  const children = [
    ...existingChildren,
    {
      ...childRest,
      is_inside_bundle: true,
      is_bundle: false,
      product_service_parent_id: [String(parent[ID_KEY])]
    }
  ];

  // Add-to-bundle only appends to children (see document product-service add flow).
  // bundle_group is for optional grouped selections, not every bundled child.
  const bundle_group = parent.bundle_group;

  const updatedParent = {
    ...parent,
    children,
    is_bundle: true
  };

  const priceChanges = calculateParentItemChanges(updatedParent, bundle_group, ID_KEY);

  return {
    ...updatedParent,
    ...priceChanges,
    total: Number(priceChanges.sell || 0) * Number(parent.quantity || 1) - Number(parent.discount || 0)
  };
}

/**
 * Merges wizard add_item rows into the product service list. Items with add_to_bundle / parent id
 * are nested under the target bundle parent instead of added as top-level rows.
 */
export function mergeItemsToAddWithBundles(
  existingProductServices,
  itemsToAdd = [],
  referenceProductServiceId = {}
) {
  let list = (existingProductServices || []).map((ps) => ({ ...ps, children: [...(ps.children || [])] }));
  const pending = [];

  const findParentIndex = (parentId) =>
    list.findIndex((ps) => String(ps[ID_KEY]) === String(parentId));

  const tryAttach = (item, rawParentId) => {
    const resolvedParentId = resolveProductServiceId(rawParentId, referenceProductServiceId);
    if (!resolvedParentId) return false;

    const parentIndex = findParentIndex(resolvedParentId);
    if (parentIndex < 0) return false;

    const childPosition = getNextBundleChildPosition(list[parentIndex]);
    const child = {
      ...item,
      position: resolveProductServicePosition(item.position, childPosition),
      is_inside_bundle: true,
      is_bundle: false,
      product_service_parent_id: [String(resolvedParentId)]
    };
    list[parentIndex] = appendChildToBundleParent(list[parentIndex], child);
    return true;
  };

  const rewireNestedBundleChildren = (item) => {
    if (!item || !Array.isArray(item.children) || !item.children.length) return item;
    const parentId = item[ID_KEY];
    if (parentId == null || parentId === "") return item;
    return {
      ...item,
      product_service_parent_id: null,
      is_inside_bundle: false,
      children: item.children.map((child) => ({
        ...child,
        product_service_parent_id: [String(parentId)],
        is_inside_bundle: true,
        is_bundle: false,
        document_item_id: item.document_item_id ?? child.document_item_id,
        document_id: item.document_id ?? child.document_id
      }))
    };
  };

  (itemsToAdd || []).forEach((item) => {
    const rawParentId = getBundleParentIdFromItem(item);
    if (!rawParentId) {
      pending.push({ type: "top", item: rewireNestedBundleChildren(item) });
      return;
    }
    pending.push({ type: "child", item, rawParentId });
  });

  let progress = true;
  while (progress) {
    progress = false;
    const next = [];

    pending.forEach((entry) => {
      if (entry.type === "top") {
        list.push(entry.item);
        progress = true;
        return;
      }
      if (tryAttach(entry.item, entry.rawParentId)) {
        progress = true;
        return;
      }
      next.push(entry);
    });

    if (next.length === pending.length) {
      pending.length = 0;
      pending.push(...next);
      break;
    }
    pending.length = 0;
    pending.push(...next);
  }

  pending.forEach((entry) => {
    if (entry.type === "top") {
      list.push(entry.item);
    } else {
      list.push({
        ...entry.item,
        is_inside_bundle: true,
        is_bundle: false,
        product_service_parent_id: [
          String(resolveProductServiceId(entry.rawParentId, referenceProductServiceId))
        ]
      });
    }
  });

  return list;
}
